import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OfferDecision } from "./OfferDecision";
import { DryRun } from "./DryRun";
import { PilotPanel } from "./PilotPanel";

function Content({ it }) {
  const c = it.content;
  if (!c.retrieved) return <p className="text-xs text-slate-500" data-testid="oc-content-not-retrieved">Audit sample only: description {c.has_description ? "present" : "missing"}, images {c.has_image ? "present" : "missing"}, product URL {c.has_url ? "present" : "missing"} on the website. Full content is read only for the approved pilot.</p>;
  return (
    <div className="space-y-2 text-xs" data-testid="oc-content">
      <p><span className="font-semibold">Source:</span> WooCommerce {it.source.detail} · {new Date(it.source.fetched_at).toLocaleString()}</p>
      <p><span className="font-semibold">Product URL:</span> <a className="text-indigo-700 underline break-all" href={c.product_url} target="_blank" rel="noreferrer" data-testid="oc-content-url">{c.product_url}</a></p>
      <p data-testid="oc-content-brand"><span className="font-semibold">Brand:</span> {c.brand?.value || "—"} <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-semibold uppercase">{c.brand?.status}</span> <span className="text-slate-500">{c.brand?.source}</span></p>
      <p data-testid="oc-content-description"><span className="font-semibold">Description:</span> {c.description || "—"}</p>
      <div className="space-y-1">{c.images.map((i) => (
        <div key={i.src} className="flex gap-2 items-center" data-testid={`oc-image-${i.id}`}>
          <span className="break-all">{i.src}</span>
          {i.check && <span className={i.check.ok ? "text-emerald-700" : "text-rose-700"}>{i.check.ok ? "verified" : i.check.errors.join("; ")}</span>}
        </div>))}
      </div>
    </div>
  );
}

function History({ h }) {
  return (
    <ul className="text-xs space-y-1" data-testid="oc-history">
      {[...h].reverse().map((e, i) => <li key={i} className="border-b pb-1">{new Date(e.at).toLocaleString()} · {e.by} · {e.action}{e.offer ? ` ${e.offer}` : ""}{e.to ? ` → ${e.to}` : ""}{e.tedops_legacy_id ? ` (TedOps ${e.tedops_legacy_id})` : ""}{e.mode ? ` (${e.mode})` : ""}</li>)}
    </ul>
  );
}

export function ItemDetail({ wcId, onChanged }) {
  const [it, setIt] = useState(null); const [n, setN] = useState(0); const [vk, setVk] = useState(null);
  const load = useCallback(() => api.get(`/online-catalogue/items/${wcId}`).then((r) => setIt(r.data)), [wcId]);
  useEffect(() => { load(); }, [load]);
  if (!it) return <p className="text-sm text-slate-400 p-4">Loading…</p>;
  const changed = () => { load(); setN((x) => x + 1); onChanged(); };
  return (
    <div className="space-y-3" data-testid="oc-item-detail">
      <div><h2 className="text-lg font-semibold">{it.name}</h2><p className="text-xs text-slate-500">WooCommerce {it.wc_product_id} · {it.kind} · {it.status} · {it.categories?.join(", ")}</p></div>
      <Tabs defaultValue="link">
        <TabsList className="max-w-full overflow-x-auto justify-start"><TabsTrigger value="link" data-testid="oc-tab-link">Matching</TabsTrigger><TabsTrigger value="content" data-testid="oc-tab-content">Content</TabsTrigger><TabsTrigger value="dry" data-testid="oc-tab-dry">Google dry run</TabsTrigger><TabsTrigger value="history" data-testid="oc-tab-history">History</TabsTrigger>{it.wc_product_id === 1387 && <TabsTrigger value="pilot" data-testid="oc-tab-pilot">Google pilot</TabsTrigger>}</TabsList>
        <TabsContent value="link" className="space-y-2">{it.offers.map((o) => <OfferDecision key={o.key} wcId={it.wc_product_id} offer={o} onChange={changed} />)}</TabsContent>
        <TabsContent value="content"><Content it={it} /></TabsContent>
        <TabsContent value="dry" className="space-y-2">
          {it.offers.length > 1 && (
            <select className="text-xs border rounded px-2 py-1 max-w-full" value={vk || it.offers[0].key} onChange={(e) => setVk(e.target.value)} data-testid="oc-dry-run-offer">
              {it.offers.map((o) => <option key={o.key} value={o.key}>{o.wc_variation_id ? `Variant ${o.wc_variation_id} ${o.attributes?.join(", ") || ""}` : "Product"}</option>)}
            </select>)}
          <DryRun wcId={it.wc_product_id} offerVid={(it.offers.find((o) => o.key === vk) || it.offers[0]).wc_variation_id} refreshKey={n} />
        </TabsContent>
        <TabsContent value="history"><History h={it.history || []} /></TabsContent>
        {it.wc_product_id === 1387 && <TabsContent value="pilot"><PilotPanel wcId={1387} /></TabsContent>}
      </Tabs>
    </div>
  );
}
